import { Box } from "@chakra-ui/react-v3";
import { type ReactNode, useLayoutEffect, useRef } from "react";

// The keyframes the Skeleton recipe's `shine` variant animates
// (src/theme-v3/recipes/skeleton.ts).
const SHIMMER_KEYFRAMES = "bg-position";

const isSkeletonShimmer = (animation: Animation) =>
  animation instanceof CSSAnimation &&
  animation.animationName === SHIMMER_KEYFRAMES;

// The phase every wrapped skeleton adopts: the pre-rendered boot shell's,
// read while it is still on screen. This module loads with the boot shell,
// before React's first render replaces that markup. Without a pre-rendered
// shell (a signed-out boot shows the public one, which has none; so do the
// dev server and Storybook), the first wrapped skeleton sets it.
let shimmerStartTime: CSSNumberish | null =
  typeof document !== "undefined" && "getAnimations" in document
    ? (document.getAnimations().find(isSkeletonShimmer)?.startTime ?? null)
    : null;

export type ShimmerInPhaseProps = {
  children: ReactNode;
};

/**
 * Starts the shimmer of every Skeleton inside it in phase with the boot
 * shell's. Route skeletons replace one another as each layout loads; each new
 * one would otherwise restart its shimmer, so the wave visibly jumps at every
 * handoff. Wrap a route's whole `pendingComponent` in it. It renders a
 * `display: contents` element, so it adds no box to the layout.
 */
export function ShimmerInPhase({ children }: ShimmerInPhaseProps) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const shimmers =
      ref.current
        ?.getAnimations?.({ subtree: true })
        .filter(isSkeletonShimmer) ?? [];

    // No shimmer to follow yet. These animations haven't started, and
    // assigning a null start time would pause them, so let them run and lead
    // the ones that mount later.
    if (shimmerStartTime === null) {
      shimmers[0]?.ready
        .then((shimmer) => {
          shimmerStartTime ??= shimmer.startTime;
        })
        // Rejects when the skeleton unmounts before the animation starts.
        .catch(() => {});
      return;
    }

    for (const shimmer of shimmers) shimmer.startTime = shimmerStartTime;
  }, []);

  return (
    <Box ref={ref} display="contents">
      {children}
    </Box>
  );
}
